import { IconButton, Tooltip, TooltipProps } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';

interface HintIconProps {
    /** One note, or several — joined by a blank line, which the tooltip renders as such (pre-line). */
    notes: string | string[];
    placement?: TooltipProps['placement'];
}

/**
 * The `?` beside a heading or a control, holding a note that would otherwise sit in the page flow.
 *
 * Extracted for CALL-781, once the same Tooltip + IconButton + icon triple reached five places across the
 * two Budget pages — each needing the same two non-obvious details: an IconButton so the note is reachable
 * by keyboard (a bare icon is not a tab stop, so the tooltip would be mouse-only), and a widened tooltip,
 * since the default 300px wraps these notes into a narrow column.
 *
 * Placed ABOVE by default. A section heading sits directly on top of its grid, so the default `bottom`
 * blanketed the very columns the note describes — the same thing CALL-784 hit in ScenarioSection.
 */
const HintIcon = ({ notes, placement = 'top-start' }: HintIconProps) => {
    const { t } = useTranslation('budget');
    const title = Array.isArray(notes) ? notes.filter(Boolean).join('\n\n') : notes;

    return (
        <Tooltip
            title={title}
            placement={placement}
            slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 460 } } }}
        >
            <IconButton size="small" aria-label={t('action.showHint')}>
                <HelpOutlineIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

export default HintIcon;
